import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Autocomplete,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ShoppingCart as POIcon,
  Receipt as InvoiceIcon,
  Calculate as CalculateIcon,
  AddCircle,
  DeleteOutline,
  VerifiedUser,
  Send as SendIcon,
} from '@mui/icons-material';
import { procurementService, Vendor as ServiceVendor } from '../../services/procurementService';
import { api } from '../../services/apiClient';
import { projectService, Project } from '../../services/projectService';
import { budgetService, BudgetCheck } from '../../services/budgetService';
import { formatIndianNumber } from '../../utils/currency';
import { TypePill, StatusDot } from '../../components/common/pageKit';
import ExportButton from '../../components/common/ExportButton';
import { linkNav } from '../../utils/linkNav';
import { useTaxCalculation } from '../../hooks/useTaxCalculation';
import { TaxSummaryPanel } from '../../components/common/TaxSummaryPanel';
import {
  P,
  cardSx,
  fieldSx,
  priBtn,
  outBtn,
  gridInput,
  thSx,
  thTitle,
  PX,
  FieldLabel,
  StatusPill,
  WorkspaceHeader,
  TotalsStrip,
  ValidationStrip,
} from '../../components/common/workspaceKit';

// PO classification -- drives which line-item fields matter and how the
// backend posts the eventual invoice (see postingService.js: MATERIAL -> GR-IR
// / inventory, SERVICE|SUBCONTRACTING -> expense, ASSET|CAPEX -> Capital WIP).
type PoType =
  | 'MATERIAL'
  | 'SERVICE'
  | 'CAPEX'
  | 'SUBCONTRACTING'
  | 'JOB_WORK'
  | 'CONSUMABLE'
  | 'ASSET'
  | 'IMPORT';

type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

type LineItemType = 'MATERIAL' | 'SERVICE' | 'ASSET' | 'EXPENSE' | 'SUBCONTRACTING';

const PO_TYPES: PoType[] = ['MATERIAL', 'SERVICE', 'CAPEX', 'SUBCONTRACTING', 'JOB_WORK', 'CONSUMABLE', 'ASSET', 'IMPORT'];
const PRIORITIES: Priority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const LINE_ITEM_TYPES: LineItemType[] = ['MATERIAL', 'SERVICE', 'ASSET', 'EXPENSE', 'SUBCONTRACTING'];

const TYPE_TINT: Record<string, string> = {
  MATERIAL: '#0284c7',
  SERVICE: '#7c3aed',
  CAPEX: '#dc2626',
  SUBCONTRACTING: '#d97706',
  JOB_WORK: '#059669',
  CONSUMABLE: '#64748b',
  ASSET: '#0f766e',
  IMPORT: '#be185d',
  OTHER: '#475569',
};

const PRIORITY_COLOR: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  LOW: 'default',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

// Contextual copy explaining what each PO Type means for downstream
// receiving/accounting -- shown as an info banner so the type isn't a silent
// classification field.
const PO_TYPE_INFO: Record<PoType, string> = {
  MATERIAL: 'Goods will be received into inventory via GRN; accrues to GR-IR clearing until invoiced.',
  SERVICE: 'Recorded as a service receipt (no stock); posts to expense. TDS may apply.',
  CAPEX: 'Capitalises to Capital Work-in-Progress (1020) on invoice; asset created at capitalisation (roadmap).',
  ASSET: 'Capitalises to Capital Work-in-Progress (1020) on invoice; asset created at capitalisation (roadmap).',
  SUBCONTRACTING: 'Components issued to the vendor; processing charges post to expense (component-issue tracking is roadmap).',
  CONSUMABLE: 'Consumable stock issued for internal use; posts to expense rather than saleable inventory.',
  JOB_WORK: 'Job-work order sent to the vendor for processing; conversion charges post to expense.',
  IMPORT: 'Import purchase order; customs duty, freight and forex postings apply at goods receipt / invoice.',
};

// Default line item_type from the header PO Type -- users can still override
// per line via the line's own Select.
const defaultLineItemType = (poType: PoType): LineItemType => {
  switch (poType) {
    case 'SERVICE': return 'SERVICE';
    case 'CAPEX':
    case 'ASSET': return 'ASSET';
    case 'SUBCONTRACTING': return 'SUBCONTRACTING';
    case 'CONSUMABLE':
    case 'MATERIAL':
    default: return 'MATERIAL';
  }
};

interface PurchaseOrder {
  id: string;
  po_number: string;
  vendor_id: string;
  vendor_name: string;
  vendor_code: string;
  po_date: string;
  delivery_date?: string;
  expected_delivery_date?: string;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
  notes?: string;
  status: 'PENDING' | 'APPROVED' | 'RECEIVED' | 'CANCELLED' | string;
  approval_status?: string;
  po_type?: PoType | string;
  priority?: Priority | string;
  department?: string | null;
  cost_center_id?: string | number | null;
  cost_center_name?: string | null;
  project_id?: string | number | null;
  project_name?: string | null;
  payment_terms?: string | null;
  created_at: string;
  items?: PurchaseOrderItem[];
}

interface PurchaseOrderItem {
  id?: string;
  product_id: string;
  product_name?: string;
  item_type?: LineItemType | string;
  line_description?: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  gst_rate?: number;  // from API
  // null = "use the master rate". A number here is a deliberate override of
  // the product's tax category, which the server reports back as such.
  gstRate: number | null;
  cgst_amount?: number;
  sgst_amount?: number;
  igst_amount?: number;
  line_total: number;
  hsn_code?: string;
  line_hsn_code?: string;
  line_uom?: string;
  unit?: string;
}

interface CostCenter {
  id: number | string;
  code?: string;
  name: string;
  isActive?: boolean;
}

interface Vendor {
  id: string;
  vendor_code: string;
  company_name: string;
  contact_person: string;
  state: string;
  gstin?: string;
  tds_category: string;
  is_active?: boolean;
}

interface Product {
  id: string;
  product_name: string;
  product_type?: string;
  hsn_code: string;
  unit: string;
  unit_of_measure?: string;
  gst_rate: number;
  purchase_price?: number;
  is_active?: boolean;
}

const PurchaseOrderPage: React.FC = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // product_type allowed per PO line item_type (from /products/policy) — drives
  // the enforced "PO Type filters product selection" rule on the client.
  const [itemTypeAllowed, setItemTypeAllowed] = useState<Record<string, string[]>>({});
  const [typeRevalMsg, setTypeRevalMsg] = useState<string | null>(null);
  // Cost centers / projects are optional master data -- a tenant without
  // Finance/Projects module access (or the endpoint being unavailable) must
  // not block raising a purchase order, so both fetches fail soft to [].
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [budgetChk, setBudgetChk] = useState<BudgetCheck | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    po_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    expected_delivery_date: '',
    notes: '',
    po_type: 'MATERIAL' as PoType,
    priority: 'MEDIUM' as Priority,
    department: '',
    payment_terms: '',
    cost_center_id: '',
    project_id: '',
    items: [] as PurchaseOrderItem[],
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [calculations, setCalculations] = useState({
    subtotal: 0,
    gstAmount: 0,
    totalAmount: 0,
  });

  // Fetch data
  useEffect(() => {
    fetchPurchaseOrders();
    fetchVendors();
    fetchProducts();
    fetchPolicy();
    fetchCostCenters();
    fetchProjects();
  }, []);

  // Filter orders based on search and filters
  useEffect(() => {
    let filtered = purchaseOrders;

    if (searchTerm) {
      filtered = filtered.filter(po =>
        po.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.vendor_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (vendorFilter) {
      filtered = filtered.filter(po => po.vendor_id === vendorFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(po => po.status === statusFilter);
    }

    setFilteredOrders(filtered);
  }, [purchaseOrders, searchTerm, vendorFilter, statusFilter]);

  // Tax is DETERMINED by the server, not computed here.
  //
  // This used to be a local reduce over `taxableAmount * gstRate / 100`, with
  // the rate defaulted to 18 and typed into an editable cell. That produced a
  // single lumped GST figure and could never express the thing that actually
  // matters on a purchase order: whether the vendor's supply is intra-state
  // (CGST + SGST) or inter-state (IGST), let alone reverse charge or cess.
  const taxableLines = useMemo(
    () => formData.items.filter((it) => Number(it.quantity) > 0 && Number(it.unit_price) >= 0
      && (it.product_id || it.line_description)),
    [formData.items]
  );

  const poTaxInput = useMemo(() => {
    if (!formData.vendor_id || taxableLines.length === 0) return null;
    return {
      transactionType: 'PURCHASE_ORDER' as const,
      partyId: Number(formData.vendor_id),
      documentDate: formData.po_date,
      allowRateOverride: true,
      lines: taxableLines.map((it) => ({
        productId: it.product_id ? Number(it.product_id) : null,
        hsnCode: it.hsn_code || null,
        quantity: Number(it.quantity),
        unitPrice: Number(it.unit_price),
        discountPercent: Number(it.discount_percent) || 0,
        description: it.line_description || null,
        uom: it.unit || null,
        // Only sent when the buyer has deliberately overridden it; otherwise
        // the product's tax category decides.
        gstRate: it.gstRate != null && it.gstRate !== ('' as any) ? Number(it.gstRate) : null,
      })),
    };
  }, [formData.vendor_id, formData.po_date, taxableLines]);

  const { tax: poTax, loading: poTaxLoading, error: poTaxError } = useTaxCalculation(poTaxInput);

  useEffect(() => {
    if (poTax) {
      setCalculations({
        subtotal: poTax.subtotal,
        gstAmount: poTax.totalTax,
        totalAmount: poTax.totalAmount,
      });
      return;
    }
    // Before the first determination arrives (or with no vendor picked yet)
    // show the taxable value only rather than inventing a tax figure.
    const subtotal = formData.items.reduce((sum, item) => {
      const lineAmount = Number(item.quantity) * Number(item.unit_price);
      const discountAmount = (lineAmount * Number(item.discount_percent)) / 100;
      return sum + (lineAmount - discountAmount);
    }, 0);
    setCalculations({
      subtotal: Math.round(subtotal * 100) / 100,
      gstAmount: 0,
      totalAmount: Math.round(subtotal * 100) / 100,
    });
  }, [poTax, formData.items]);

  /** The server's figures for one grid row, once they arrive. */
  const poTaxLineAt = (index: number) => {
    if (!poTax) return null;
    const activeIndex = taxableLines.indexOf(formData.items[index]);
    return activeIndex >= 0 ? poTax.lines[activeIndex] || null : null;
  };

  // Advisory budget check for the PO — non-blocking, net-of-tax, against the
  // PO-date's FY. Only while composing (not viewing) and once a cost center /
  // project and a positive value are set.
  useEffect(() => {
    if (!openDialog || dialogMode === 'view') { setBudgetChk(null); return undefined; }
    const cc = formData.cost_center_id ? Number(formData.cost_center_id) : undefined;
    const pj = formData.project_id ? Number(formData.project_id) : undefined;
    const amt = calculations.subtotal;
    if ((!cc && !pj) || amt <= 0 || !formData.po_date) { setBudgetChk(null); return undefined; }
    const t = setTimeout(() => {
      budgetService.check({ date: formData.po_date, amount: amt, costCenterId: cc, projectId: pj })
        .then(setBudgetChk).catch(() => setBudgetChk(null));
    }, 400);
    return () => clearTimeout(t);
  }, [openDialog, dialogMode, formData.cost_center_id, formData.project_id, formData.po_date, calculations.subtotal]);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ purchaseOrders: PurchaseOrder[] }>('/procurement/purchase-orders');
      setPurchaseOrders(response.purchaseOrders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  // Send a draft PO for approval straight from the list. Previously this only
  // existed on the PO detail page, so clearing a backlog of drafts meant
  // opening each one. The API re-checks eligibility (status and approval_status
  // must both be DRAFT) and is the authority -- a 409 from it is surfaced as-is,
  // which is also how an already-submitted PO explains itself here.
  const [submittingPoId, setSubmittingPoId] = useState<number | string | null>(null);

  const handleSubmitForApproval = async (po: PurchaseOrder) => {
    setSubmittingPoId(po.id);
    try {
      const res = await api.post<{ autoApproved?: boolean }>(
        `/procurement/purchase-orders/${po.id}/submit`
      );
      setSnackbar({
        open: true,
        message: res?.autoApproved
          ? `${po.po_number}: no matching approval workflow — auto-approved.`
          : `${po.po_number} submitted for approval.`,
        severity: 'success',
      });
      await fetchPurchaseOrders();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to submit for approval',
        severity: 'error',
      });
    } finally {
      setSubmittingPoId(null);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await procurementService.getVendors();
      setVendors(response.vendors.filter((vendor: ServiceVendor) => vendor.is_active));
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get<{ products: Product[] }>('/products');
      setProducts(response.products.filter((product: Product) => product.is_active));
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchPolicy = async () => {
    try {
      const r = await api.get<{ itemTypeAllowedProductTypes: Record<string, string[]> }>('/products/policy');
      setItemTypeAllowed(r.itemTypeAllowedProductTypes || {});
    } catch (err) {
      console.error('Failed to fetch product policy:', err);
    }
  };

  // The enforced rule, client side: which products a line of a given item_type
  // may select. Unknown item_type or an unclassified product -> permissive.
  const allowedProductsFor = (lineType: string): Product[] => {
    const allowed = itemTypeAllowed[lineType];
    if (!allowed || !allowed.length) return products;
    return products.filter((p) => !p.product_type || allowed.includes(p.product_type));
  };
  const productAllowedForType = (productId: any, lineType: string): boolean => {
    if (!productId) return true;
    const p = products.find((x) => x.id === productId);
    const allowed = itemTypeAllowed[lineType];
    if (!p || !p.product_type || !allowed) return true;
    return allowed.includes(p.product_type);
  };

  const fetchCostCenters = async () => {
    try {
      const response = await api.get<{ costCenters: CostCenter[] }>('/cost-centers');
      setCostCenters((response.costCenters || []).filter((c) => c.isActive !== false));
    } catch (err) {
      // Optional master data -- module may not be enabled for this tenant.
      setCostCenters([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const list = await projectService.getProjects();
      setProjects(list || []);
    } catch (err) {
      // Optional master data -- module may not be enabled for this tenant.
      setProjects([]);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.vendor_id) {
      errors.vendor_id = 'Vendor is required';
    }

    if (!formData.po_date) {
      errors.po_date = 'PO date is required';
    }

    if (formData.items.length === 0) {
      errors.items = 'At least one item is required';
    }

    formData.items.forEach((item, index) => {
      const lineType = (item.item_type as LineItemType) || 'MATERIAL';
      if (lineType === 'MATERIAL') {
        if (!item.product_id) {
          errors[`item_${index}_product`] = 'Item is required';
        }
      } else if (!item.line_description || !item.line_description.trim()) {
        errors[`item_${index}_description`] = 'Description is required';
      }
      if (item.quantity <= 0) {
        errors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unit_price <= 0) {
        errors[`item_${index}_price`] = 'Unit price must be greater than 0';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      // Transform snake_case to camelCase for backend
      const payload = {
        vendorId: formData.vendor_id,
        poDate: formData.po_date,
        deliveryDate: formData.delivery_date || null,
        expectedDeliveryDate: formData.expected_delivery_date || null,
        notes: formData.notes,
        poType: formData.po_type,
        priority: formData.priority,
        department: formData.department || null,
        paymentTerms: formData.payment_terms || null,
        costCenterId: formData.cost_center_id || null,
        projectId: formData.project_id || null,
        items: formData.items.map(item => ({
          productId: item.product_id,
          itemType: item.item_type || defaultLineItemType(formData.po_type),
          lineDescription: item.line_description || '',
          hsnCode: item.hsn_code || '',
          uom: item.unit || '',
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercent: item.discount_percent,
          // Only sent when the buyer typed an override; otherwise the server
          // resolves the rate from the product's tax category.
          gstRate: item.gstRate == null || item.gstRate === ('' as any) ? null : Number(item.gstRate)
        }))
      };

      if (dialogMode === 'create') {
        const response = await api.post<{ purchaseOrder: PurchaseOrder }>('/procurement/purchase-orders', payload);
        setSnackbar({ open: true, message: 'Purchase order created successfully', severity: 'success' });
        setOpenDialog(false);
        resetForm();
        const newId = response?.purchaseOrder?.id;
        if (newId) {
          // New POs are created as DRAFT -- submission/approval happens on
          // the PO Detail page, so route there instead of just closing.
          navigate(`/procurement/orders/${newId}`);
          return;
        }
        await fetchPurchaseOrders();
      } else {
        await api.put(`/procurement/purchase-orders/${selectedPO?.id}`, payload);
        setSnackbar({ open: true, message: 'Purchase order updated successfully', severity: 'success' });
        setOpenDialog(false);
        resetForm();
        await fetchPurchaseOrders();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save purchase order';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      po_date: new Date().toISOString().split('T')[0],
      delivery_date: '',
      expected_delivery_date: '',
      notes: '',
      po_type: 'MATERIAL',
      priority: 'MEDIUM',
      department: '',
      payment_terms: '',
      cost_center_id: '',
      project_id: '',
      items: [],
    });
    setFormErrors({});
    setSelectedPO(null);
  };

  const handleCreate = () => {
    setDialogMode('create');
    resetForm();
    setOpenDialog(true);
  };

  const addItem = () => {
    setFormData(prev => {
      const newItem: PurchaseOrderItem = {
        product_id: '',
        item_type: defaultLineItemType(prev.po_type),
        line_description: '',
        hsn_code: '',
        unit: '',
        quantity: 1,
        unit_price: 0,
        discount_percent: 0,
        gstRate: null,
        line_total: 0,
      };
      return { ...prev, items: [...prev.items, newItem] };
    });
  };

  const updateItem = (index: number, field: keyof PurchaseOrderItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const next: any = { ...item, [field]: value };
        // Changing the line item_type may invalidate the selected product.
        if (field === 'item_type' && next.product_id && !productAllowedForType(next.product_id, String(value))) {
          next.product_id = '';
          setTypeRevalMsg(`Line ${index + 1}: the selected product is not valid for a ${value} line and was cleared.`);
        }
        return next;
      }),
    }));
  };

  // Revalidate every line against a (new) PO Type: re-default each line's
  // item_type to match and clear any product now invalid. Surfaces a message
  // (the backend enforces the same rule regardless).
  const changePoType = (newPoType: PoType) => {
    const newLineType = defaultLineItemType(newPoType);
    let cleared = 0;
    setFormData(prev => ({
      ...prev,
      po_type: newPoType,
      items: prev.items.map((item) => {
        const next: any = { ...item, item_type: newLineType };
        if (next.product_id && !productAllowedForType(next.product_id, newLineType)) {
          next.product_id = '';
          cleared += 1;
        }
        return next;
      }),
    }));
    setTypeRevalMsg(cleared > 0
      ? `${cleared} line(s) had a product not valid for ${newPoType} purchase orders — those products were cleared.`
      : null);
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'info';
      case 'RECEIVED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // ---------------------------------------------------------------------
  // Create / Edit / View — full-width inline workspace (replaces the old
  // modal dialog). Purely presentational: formData/calculations/handlers
  // below are untouched, only how they're laid out changes.
  // ---------------------------------------------------------------------
  if (openDialog) {
    const closeWorkspace = () => {
      setOpenDialog(false);
      resetForm();
    };

    const poStatusTone = (status?: string): 'draft' | 'green' | 'amber' | 'red' | 'muted' | 'primary' => {
      switch (status) {
        case 'PENDING': return 'amber';
        case 'APPROVED': return 'primary';
        case 'RECEIVED': return 'green';
        case 'CANCELLED': return 'red';
        case 'DRAFT': return 'draft';
        default: return 'muted';
      }
    };

    const isLineValid = (item: PurchaseOrderItem) => {
      const lineType = (item.item_type as LineItemType) || 'MATERIAL';
      const hasIdentity = lineType === 'MATERIAL'
        ? !!item.product_id
        : !!(item.line_description && item.line_description.trim());
      return hasIdentity && Number(item.quantity) > 0 && Number(item.unit_price) > 0;
    };

    const statusLabel = dialogMode === 'view' ? (selectedPO?.status || '—') : 'DRAFT';

    return (
      <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
        <WorkspaceHeader
          onBack={closeWorkspace}
          title={
            dialogMode === 'create'
              ? 'Create Purchase Order'
              : dialogMode === 'edit'
              ? 'Edit Purchase Order'
              : `Purchase Order — ${selectedPO?.po_number}`
          }
          subtitle="PO Type drives how goods/services are received and posted downstream — pick it first."
          chip={<StatusPill label={statusLabel} tone={poStatusTone(statusLabel)} />}
          actions={
            dialogMode !== 'view' && (
              <>
                <Button variant="outlined" onClick={closeWorkspace} disabled={submitting} sx={outBtn}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <VerifiedUser sx={{ fontSize: 18 }} />}
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={priBtn}
                >
                  {submitting ? 'Saving…' : dialogMode === 'create' ? 'Create Purchase Order' : 'Update Purchase Order'}
                </Button>
              </>
            )
          }
        />

        <Box sx={{ px: { xs: 2, md: 2 }, pt: 2.5 }}>
          {formErrors.items && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
              {formErrors.items}
            </Alert>
          )}

          {typeRevalMsg && (
            <Alert severity="warning" onClose={() => setTypeRevalMsg(null)} sx={{ mb: 2, borderRadius: '10px' }}>
              {typeRevalMsg}
            </Alert>
          )}

          {budgetChk && budgetChk.hasBudget && (
            <Alert severity={budgetChk.exceeds ? 'warning' : 'info'} sx={{ mb: 2, borderRadius: '10px' }}>
              {budgetChk.exceeds
                ? `This PO would exceed the available budget by ${formatIndianNumber(budgetChk.overBy)}. `
                : 'Within available budget. '}
              <Box component="span" sx={{ color: 'text.secondary' }}>
                Budget {formatIndianNumber(budgetChk.budget)} · Actual {formatIndianNumber(budgetChk.actual)}
                {budgetChk.committed > 0 ? ` · Committed ${formatIndianNumber(budgetChk.committed)}` : ''} · Available {formatIndianNumber(budgetChk.available)}
                {budgetChk.projectedConsumption != null ? ` · would reach ${budgetChk.projectedConsumption}% consumed` : ''}
              </Box>
            </Alert>
          )}

          {/* Metadata */}
          <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
            <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Order Details</Typography>
            </Box>
            <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
              <Box>
                <FieldLabel text="PO Type" />
                <TextField select fullWidth size="small" value={formData.po_type} onChange={(e) => changePoType(e.target.value as PoType)} disabled={dialogMode === 'view'} sx={fieldSx}>
                  {PO_TYPES.map(t => (
                    <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <FieldLabel text="Priority" />
                <TextField select fullWidth size="small" value={formData.priority} onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as Priority }))} disabled={dialogMode === 'view'} sx={fieldSx}>
                  {PRIORITIES.map(p => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2' } }}>
                <FieldLabel text="Vendor" required />
                <Autocomplete
                  size="small"
                  value={vendors.find(v => v.id === formData.vendor_id) || null}
                  onChange={(event, newValue) => setFormData(prev => ({ ...prev, vendor_id: newValue?.id || '' }))}
                  options={vendors}
                  getOptionLabel={(option) => `${option.company_name} (${option.vendor_code})`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={dialogMode === 'view'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select vendor"
                      error={!!formErrors.vendor_id}
                      helperText={formErrors.vendor_id}
                      sx={fieldSx}
                    />
                  )}
                />
              </Box>

              <Box sx={{ gridColumn: '1 / -1' }}>
                <Alert severity="info" sx={{ borderRadius: '10px' }}>
                  {PO_TYPE_INFO[formData.po_type] || PO_TYPE_INFO.MATERIAL}
                </Alert>
              </Box>

              <Box>
                <FieldLabel text="PO Date" required />
                <TextField
                  type="date"
                  fullWidth
                  size="small"
                  value={formData.po_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, po_date: e.target.value }))}
                  error={!!formErrors.po_date}
                  helperText={formErrors.po_date}
                  disabled={dialogMode === 'view'}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <FieldLabel text="Delivery Date" />
                <TextField
                  type="date"
                  fullWidth
                  size="small"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                  disabled={dialogMode === 'view'}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <FieldLabel text="Expected Delivery Date" />
                <TextField
                  type="date"
                  fullWidth
                  size="small"
                  value={formData.expected_delivery_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                  disabled={dialogMode === 'view'}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <FieldLabel text="Payment Terms" />
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Net 30"
                  value={formData.payment_terms}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                  disabled={dialogMode === 'view'}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <FieldLabel text="Department" />
                <TextField
                  fullWidth
                  size="small"
                  value={formData.department}
                  onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  disabled={dialogMode === 'view'}
                  sx={fieldSx}
                />
              </Box>
              {(costCenters.length > 0 || (dialogMode === 'view' && selectedPO?.cost_center_name)) && (
                <Box>
                  <FieldLabel text="Cost Center" />
                  {dialogMode === 'view' ? (
                    <TextField fullWidth size="small" value={selectedPO?.cost_center_name || 'None'} disabled sx={fieldSx} />
                  ) : (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={formData.cost_center_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost_center_id: e.target.value }))}
                      sx={fieldSx}
                    >
                      <MenuItem value="">None</MenuItem>
                      {costCenters.map(cc => (
                        <MenuItem key={cc.id} value={cc.id}>
                          {cc.code ? `${cc.code} - ${cc.name}` : cc.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
              )}
              {(projects.length > 0 || (dialogMode === 'view' && selectedPO?.project_name)) && (
                <Box>
                  <FieldLabel text="Project" />
                  {dialogMode === 'view' ? (
                    <TextField fullWidth size="small" value={selectedPO?.project_name || 'None'} disabled sx={fieldSx} />
                  ) : (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={formData.project_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, project_id: e.target.value }))}
                      sx={fieldSx}
                    >
                      <MenuItem value="">None</MenuItem>
                      {projects.map(p => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.project_code} - {p.project_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
              )}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <FieldLabel text="Notes / Terms" />
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  disabled={dialogMode === 'view'}
                  sx={fieldSx}
                />
              </Box>
            </Box>
          </Paper>

          {/* Line items — full width */}
          <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${P.line}` }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Purchase Order Items</Typography>
              {dialogMode !== 'view' && (
                <Button size="small" startIcon={<AddCircle sx={{ fontSize: 18 }} />} onClick={addItem} sx={{ ...outBtn, height: 34 }}>
                  Add Item
                </Button>
              )}
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 1180, '& .MuiOutlinedInput-root': { fontSize: '13px' }, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 36 }}><Typography sx={thTitle}>#</Typography></TableCell>
                    <TableCell sx={{ ...thSx, width: 120 }}><Typography sx={thTitle}>Item Type</Typography></TableCell>
                    <TableCell sx={thSx}><Typography sx={thTitle}>Product / Service</Typography></TableCell>
                    <TableCell sx={thSx}><Typography sx={thTitle}>Description</Typography></TableCell>
                    <TableCell sx={{ ...thSx, width: 90 }}><Typography sx={thTitle}>HSN/SAC</Typography></TableCell>
                    <TableCell sx={{ ...thSx, width: 84 }}><Typography sx={thTitle}>UOM</Typography></TableCell>
                    <TableCell align="right" sx={{ ...thSx, width: 88 }}><Typography sx={thTitle}>Qty</Typography></TableCell>
                    <TableCell align="right" sx={{ ...thSx, width: 100 }}><Typography sx={thTitle}>Rate</Typography></TableCell>
                    <TableCell align="right" sx={{ ...thSx, width: 70 }}><Typography sx={thTitle}>Disc %</Typography></TableCell>
                    <TableCell align="right" sx={{ ...thSx, width: 70 }}><Typography sx={thTitle}>GST %</Typography></TableCell>
                    <TableCell align="right" sx={{ ...thSx, width: 120 }}><Typography sx={thTitle}>Line Total</Typography></TableCell>
                    {dialogMode !== 'view' && <TableCell sx={{ ...thSx, width: 44 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => {
                    const lineType = (item.item_type as LineItemType) || 'MATERIAL';
                    const isMaterial = lineType === 'MATERIAL';
                    const taxLine = poTaxLineAt(index);
                    const resolvedRate = taxLine ? taxLine.gstRate : null;
                    return (
                      <TableRow key={index} sx={{ '& td': { borderBottom: `1px solid ${P.tline}`, py: 0.5 } }}>
                        <TableCell sx={{ color: P.muted, fontSize: '13px' }}>{index + 1}</TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={lineType}
                            onChange={(e) => updateItem(index, 'item_type', e.target.value)}
                            disabled={dialogMode === 'view'}
                            sx={gridInput}
                          >
                            {LINE_ITEM_TYPES.map(t => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            value={products.find(p => p.id === item.product_id) || null}
                            onChange={(event, newValue) => {
                              updateItem(index, 'product_id', newValue?.id || '');
                              // The rate is NOT copied onto the line — it is
                              // resolved server-side from the product's tax
                              // category, so it stays correct when the vendor
                              // (and therefore the CGST/SGST-vs-IGST split) or
                              // the document date changes.
                              updateItem(index, 'gstRate', null);
                              updateItem(index, 'unit_price', newValue?.purchase_price || 0);
                              // UOM + HSN always come from the product master.
                              updateItem(index, 'unit', newValue?.unit_of_measure || newValue?.unit || '');
                              updateItem(index, 'hsn_code', newValue?.hsn_code || '');
                            }}
                            options={allowedProductsFor(lineType)}
                            getOptionLabel={(option) => `${option.product_name} (${option.hsn_code})`}
                            disabled={dialogMode === 'view'}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={isMaterial ? 'Select item' : `Select ${lineType.toLowerCase()} item`}
                                error={!!formErrors[`item_${index}_product`]}
                                sx={gridInput}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={lineType === 'ASSET' ? 'Asset detail' : 'Line description'}
                            value={item.line_description || ''}
                            onChange={(e) => updateItem(index, 'line_description', e.target.value)}
                            error={!!formErrors[`item_${index}_description`]}
                            disabled={dialogMode === 'view'}
                            sx={gridInput}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={item.hsn_code || ''}
                            onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                            disabled={dialogMode === 'view'}
                            sx={gridInput}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={item.unit || ''}
                            onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            disabled={dialogMode === 'view'}
                            sx={gridInput}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            error={!!formErrors[`item_${index}_quantity`]}
                            disabled={dialogMode === 'view'}
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={gridInput}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            error={!!formErrors[`item_${index}_price`]}
                            disabled={dialogMode === 'view'}
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={gridInput}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={item.discount_percent}
                            onChange={(e) => updateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                            disabled={dialogMode === 'view'}
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={gridInput}
                          />
                        </TableCell>
                        {/* GST% is RESOLVED from the product's tax category and
                            shown as the placeholder. Procurement genuinely does
                            negotiate concessional rates, so the cell stays
                            editable — but leaving it blank means "use the
                            master", which is now the default rather than a
                            hardcoded 18. */}
                        <TableCell align="right">
                          <Tooltip title={resolvedRate != null ? `Master rate ${resolvedRate}% — type only to override it` : 'Pick a product and a vendor'} arrow>
                            <TextField
                              size="small"
                              type="number"
                              fullWidth
                              placeholder={resolvedRate != null ? String(resolvedRate) : 'auto'}
                              value={item.gstRate ?? ''}
                              onChange={(e) => updateItem(index, 'gstRate', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                              disabled={dialogMode === 'view'}
                              inputProps={{ style: { textAlign: 'right' } }}
                              sx={{ ...gridInput, '& input::placeholder': { color: P.muted, opacity: 1, fontWeight: 600 } }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: P.strong, whiteSpace: 'nowrap', fontSize: '13.5px' }}>
                          {taxLine ? `₹${taxLine.lineTotal.toFixed(2)}` : '—'}
                        </TableCell>
                        {dialogMode !== 'view' && (
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => removeItem(index)} sx={{ color: P.muted, '&:hover': { color: P.red } }}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {formData.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={dialogMode !== 'view' ? 12 : 11} align="center" sx={{ py: 3, color: P.muted, fontSize: '13px' }}>
                        No line items yet{dialogMode !== 'view' ? ' — click "Add Item" to start.' : '.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Totals + the server-determined tax breakup. The strip keeps the
              at-a-glance figures; the panel beside it shows the CGST/SGST vs
              IGST split, reverse charge, and WHY the engine decided that. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 380px' }, gap: 1.5, mb: 1.5 }}>
            <TotalsStrip
              cells={[
                { label: 'Subtotal', value: `₹${calculations.subtotal.toFixed(2)}` },
                { label: 'Tax (GST)', value: `₹${calculations.gstAmount.toFixed(2)}` },
                { label: 'Total', value: `₹${calculations.totalAmount.toFixed(2)}`, tone: 'primary' },
                { label: 'Status', value: statusLabel },
              ]}
            />
            <TaxSummaryPanel tax={poTax} loading={poTaxLoading} error={poTaxError} />
          </Box>

          {/* Validation strip */}
          <ValidationStrip
            items={[
              { ok: !!formData.vendor_id, label: 'Vendor selected' },
              { ok: !!formData.po_date, label: 'PO date set' },
              { ok: formData.items.length > 0, label: 'At least one line' },
              { ok: formData.items.length > 0 && formData.items.every(isLineValid), label: 'All lines valid' },
            ]}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Purchase Orders
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ExportButton
            rows={filteredOrders as any}
            columns={[
              { header: 'PO Number', key: 'po_number' }, { header: 'Vendor', key: 'vendor_name' },
              { header: 'Type', key: 'po_type' }, { header: 'Total', key: 'total_amount' },
              { header: 'Approval', key: 'approval_status' }, { header: 'Status', key: 'status' },
            ]}
            filename="purchase-orders"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Create Purchase Order
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <POIcon color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {purchaseOrders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total POs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <POIcon color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {purchaseOrders.filter(po => po.status === 'PENDING').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InvoiceIcon color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {purchaseOrders.filter(po => po.status === 'RECEIVED').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Received
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CalculateIcon color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    ₹{(purchaseOrders.reduce((sum, po) => sum + (Number(po.total_amount) || 0), 0) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Value
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Search Purchase Orders"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  label="Vendor"
                >
                  <MenuItem value="">All Vendors</MenuItem>
                  {vendors.map(vendor => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="RECEIVED">Received</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredOrders.length} of {purchaseOrders.length} orders
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>PO Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>PO Date</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Received</TableCell>
              <TableCell>Approval</TableCell>
              <TableCell>Exception</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((po) => (
              <TableRow key={po.id} hover>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    {...linkNav(`/procurement/orders/${po.id}`, navigate)}
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {po.po_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    <TypePill
                      label={(po.po_type || 'MATERIAL').toString().replace(/_/g, ' ')}
                      tint={TYPE_TINT[po.po_type || 'MATERIAL'] || TYPE_TINT.OTHER}
                    />
                    {po.priority && (
                      <Chip
                        label={po.priority}
                        color={PRIORITY_COLOR[po.priority] || 'default'}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {po.vendor_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {po.vendor_code}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(po.po_date).toLocaleDateString('en-IN')}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    ₹{(Number(po.total_amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={po.status}
                    color={getStatusColor(po.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {po.status === 'FULLY_RECEIVED' || po.status === 'RECEIVED' || po.status === 'DELIVERED' ? (
                    <StatusDot label="Full" tone="success" />
                  ) : po.status === 'PARTIALLY_RECEIVED' ? (
                    <StatusDot label="Partial" tone="warning" />
                  ) : po.status === 'CANCELLED' ? (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  ) : (
                    <StatusDot label="Not received" tone="neutral" />
                  )}
                </TableCell>
                <TableCell>
                  {po.approval_status ? (
                    <Chip
                      label={po.approval_status.replace(/_/g, ' ')}
                      color={po.approval_status === 'APPROVED' ? 'success' : po.approval_status === 'REJECTED' ? 'error' : po.approval_status === 'PENDING_APPROVAL' ? 'warning' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {(po.approval_status === 'PENDING_APPROVAL' || po.status === 'REJECTED') ? (
                    <StatusDot
                      label={po.status === 'REJECTED' ? 'Rejected' : 'Awaiting Approval'}
                      tone="error"
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Open purchase order">
                      <IconButton size="small" {...linkNav(`/procurement/orders/${po.id}`, navigate)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {po.status === 'DRAFT' && (
                      <Tooltip
                        title={
                          !po.approval_status || po.approval_status === 'DRAFT'
                            ? 'Submit for approval'
                            : `Already submitted — approval is ${po.approval_status.replace(/_/g, ' ').toLowerCase()}`
                        }
                      >
                        {/* span so the tooltip still fires while the button is disabled */}
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={
                              submittingPoId !== null ||
                              !(!po.approval_status || po.approval_status === 'DRAFT')
                            }
                            onClick={() => handleSubmitForApproval(po)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No purchase orders found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseOrderPage;
